import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { AppLoader } from '../../../Components/common/AppLoader/AppLoader';
import { VideoCard } from '../../../Components/ui/VideoCard/VideoCard';
import { fetchDigitalCme } from '../../../api/cmeApi';
import { fetchDoctors, fetchSpecialties } from '../../../api/doctorApi';
import { getSpecialtyNames } from '../../../utils/doctorHelpers';
import { mapCmeToVideoCardProps } from '../../admin/utils/digitalCmeHelpers';
import './empoweringdoctors.css';

const buildDoctorsById = (doctors = []) =>
  Object.fromEntries(doctors.map((doc) => [doc.doctor_id, doc]));

export const EmpoweringDoctors = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('');
  const [videoCards, setVideoCards] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [cmeRows, doctorRows, specialtyRows] = await Promise.all([
          fetchDigitalCme(),
          fetchDoctors(),
          fetchSpecialties(),
        ]);
        if (cancelled) return;

        const doctorsById = buildDoctorsById(doctorRows);
        const specialtyNames = getSpecialtyNames(specialtyRows);
        const mappedCards = cmeRows.map((cme) => ({
          ...mapCmeToVideoCardProps(cme, doctorsById),
          specialties: (cme.specialties || [])
            .map((s) => s.medical_specialty)
            .filter(Boolean),
        }));

        setVideoCards(mappedCards);
        setSpecialties(specialtyNames);
        setActiveTab(
          specialtyNames[0]
            || mappedCards.find((card) => card.specialties.length > 0)?.specialties[0]
            || ''
        );
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load CME content.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const filteredVideoCards = useMemo(() => {
    if (!activeTab) return videoCards;
    return videoCards.filter((card) => card.specialties.includes(activeTab));
  }, [videoCards, activeTab]);

  return (
    <section className="empowering">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2 className="empowering__title">Empowering Doctors with Evidence-Based Knowledge</h2>
            <p className="empowering__subtitle">
              Learn from leading doctors and specialists through focused, digestible video content.
            </p>
          </Col>
        </Row>

        <div className="empowering__tabs-wrap">
          <div className="empowering__tabs">
            {specialties.map((s) => (
              <button
                key={s}
                type="button"
                className={`empowering__tab ${activeTab === s ? 'empowering__tab--active' : ''}`}
                onClick={() => setActiveTab(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <AppLoader className="py-5" />
        )}

        {!loading && error && (
          <p className="text-center py-4 text-danger">{error}</p>
        )}

        {!loading && !error && (
          <div className="empowering__scroll-wrap">
            <div className="empowering__track">
              {filteredVideoCards.length > 0 ? (
                filteredVideoCards.map((card) => (
                  <div
                    key={card.id}
                    className="empowering__card-item empowering__card-item--clickable"
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/cme/${card.slug}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/cme/${card.slug}`);
                      }
                    }}
                  >
                    <VideoCard {...card} />
                  </div>
                ))
              ) : (
                <p className="empowering__empty">
                  {activeTab
                    ? 'No CME content available for this specialty.'
                    : 'No CME content available yet.'}
                </p>
              )}
            </div>
          </div>
        )}
      </Container>
    </section>
  );
};
